import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePushNotification from '../hooks/usePushNotification';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHome, FaRedo, FaShareAlt, FaLightbulb, FaWifi } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Timer from '../components/UI/Timer';
import CoinDisplay from '../components/UI/CoinDisplay';
import QuestionCard from '../components/Quiz/QuestionCard';
import ScorePopup from '../components/Quiz/ScorePopup';
import Button from '../components/UI/Button';
import api from '../api/axios';
import useTimer from '../hooks/useTimer';
import useSound from '../hooks/useSound';
import { useAuth } from '../context/AuthContext';
import LevelUpModal from '../components/UI/LevelUpModal';
import useLevelUp from '../hooks/useLevelUp';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { saveQuestionsToCache, loadQuestionsFromCache } from '../utils/questionCache';
import * as animations from '../utils/animations';

const TOTAL_QUESTIONS     = 10;
const SURVIVAL_QUESTIONS  = 50;
const TIME_CLASSIC        = 20;
const TIME_SURVIVAL_START = 10;
const TIME_SURVIVAL_MIN   = 3;
const SURVIVAL_LIVES      = 3;
const MAX_CONTINUES       = 3;   // reprises maximum par partie de survie

function shuffleOptions(question) {
  const options = [...(question.options || question.reponses || [])];
  const correctIdx = question.reponseCorrecte ?? question.correctAnswer ?? 0;
  const correctText = options[correctIdx];

  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }

  return { ...question, options, reponseCorrecte: options.indexOf(correctText) };
}

const DEMO_QUESTIONS = [
  {
    _id: 'q1', question: "Quel pays a remporté la Coupe du Monde 2018 ?",
    options: ["Allemagne", "France", "Brésil", "Argentine"],
    reponseCorrecte: 1, difficulte: 'facile', categorie: 'Coupes du Monde',
  },
  {
    _id: 'q2', question: "Combien de joueurs compte une équipe de football sur le terrain ?",
    options: ["9", "10", "11", "12"],
    reponseCorrecte: 2, difficulte: 'facile', categorie: 'Règles',
  },
  {
    _id: 'q3', question: "Quel club a remporté le plus de Ligues des Champions ?",
    options: ["FC Barcelone", "Bayern Munich", "Real Madrid", "AC Milan"],
    reponseCorrecte: 2, difficulte: 'moyen', categorie: 'Champions League',
  },
  {
    _id: 'q4', question: "Dans quelle ville se trouve le stade San Siro ?",
    options: ["Rome", "Turin", "Naples", "Milan"],
    reponseCorrecte: 3, difficulte: 'moyen', categorie: 'Stades',
  },
  {
    _id: 'q5', question: "Qui est le meilleur buteur de l'histoire de la Coupe du Monde ?",
    options: ["Pelé", "Ronaldo (Brésilien)", "Miroslav Klose", "Just Fontaine"],
    reponseCorrecte: 2, difficulte: 'difficile', categorie: 'Records',
  },
  {
    _id: 'q6', question: "En quelle année a eu lieu la première Coupe du Monde ?",
    options: ["1920", "1924", "1930", "1934"],
    reponseCorrecte: 2, difficulte: 'difficile', categorie: 'Histoire',
  },
  {
    _id: 'q7', question: "Quel joueur a remporté le plus de Ballons d'Or ?",
    options: ["Cristiano Ronaldo", "Lionel Messi", "Ronaldinho", "Zinedine Zidane"],
    reponseCorrecte: 1, difficulte: 'moyen', categorie: 'Récompenses',
  },
  {
    _id: 'q8', question: "Quel pays organise la Coupe du Monde 2026 ?",
    options: ["Qatar", "Arabie Saoudite", "États-Unis/Canada/Mexique", "Australie"],
    reponseCorrecte: 2, difficulte: 'facile', categorie: 'Actualités',
  },
  {
    _id: 'q9', question: "Combien de cartons jaunes mènent à une expulsion ?",
    options: ["1", "2", "3", "4"],
    reponseCorrecte: 1, difficulte: 'facile', categorie: 'Règles',
  },
  {
    _id: 'q10', question: "Quel stade a la plus grande capacité au monde ?",
    options: ["Camp Nou", "Wembley", "Rungrado 1er Mai", "Rose Bowl"],
    reponseCorrecte: 2, difficulte: 'difficile', categorie: 'Stades',
  },
];

function Quiz() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const { checkLevelUp, levelUpData, clearLevelUp } = useLevelUp();
  const { playCorrect, playIncorrect, playTick, playVictory } = useSound();

  const mode = searchParams.get('mode') || 'classic';
  const isSurvivalMode = mode === 'timed';
  const isOnline = useOnlineStatus();
  const isPremiumUser = user?.plan === 'premium' || user?.role === 'admin';

  // États du jeu
  const [gameStarted, setGameStarted]   = useState(false);
  const [isOfflineGame, setIsOfflineGame]     = useState(false);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const pendingStartRef = useRef(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState('mixte');
  const [questions, setQuestions]       = useState([]);
  const [gameId, setGameId]             = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [revealed, setRevealed]         = useState(false);
  const [score, setScore]               = useState(0);
  const [streak, setStreak]             = useState(0);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [lastPoints, setLastPoints]     = useState(25);
  const [gameOver, setGameOver]         = useState(false);
  const [loading, setLoading]           = useState(true);
  const [results, setResults]           = useState([]);

  // Indices 50/50
  const [eliminatedOptions, setEliminatedOptions] = useState([]);
  const [hintUsed, setHintUsed]         = useState(false);

  // Survival : la partie se joue TOUJOURS avec 3 vies internes. Le stock du
  // compte (user.lives) sert à payer l'entrée et les reprises, il n'est plus
  // utilisé comme nombre de vies dans la partie — ce qui vidait tout le stock
  // à chaque survie, puisqu'une survie finit forcément à zéro vie.
  const userLives = SURVIVAL_LIVES;
  const [lives, setLives]               = useState(SURVIVAL_LIVES);
  const livesRef                        = useRef(SURVIVAL_LIVES);
  // Reprise après élimination : coûte 1 vie du stock, 3 fois maximum
  const [offerContinue, setOfferContinue] = useState(false);
  const continuesUsedRef                  = useRef(0);
  // Réponses dont l'appel /answer n'a pas abouti (réseau) : renvoyées à la fin
  // pour que le SERVEUR les revalide et recalcule les points lui-même.
  const missedAnswersRef                = useRef([]);
  const [currentTimePerQ, setCurrentTimePerQ] = useState(
    isSurvivalMode ? TIME_SURVIVAL_START : TIME_CLASSIC
  );
  const currentTimePerQRef = useRef(isSurvivalMode ? TIME_SURVIVAL_START : TIME_CLASSIC);
  const totalAnsweredRef   = useRef(0);

  const currentQuestion      = questions[currentIndex];
  const nextQuestionRef      = useRef(null);
  const finishSurvivalRef    = useRef(null);

  // Timer
  const handleTimeUp = useCallback(() => {
    if (!revealed) {
      playIncorrect();
      setRevealed(true);
      setSelectedAnswer(null);
      setStreak(0);
      setResults((r) => [...r, { correct: false, points: 0 }]);

      if (isSurvivalMode) {
        const newLives = livesRef.current - 1;
        livesRef.current = newLives;
        setLives(newLives);
        if (newLives <= 0) {
          // Plus de vie dans la partie : proposer de racheter une chance si le
          // joueur a encore des vies en stock et des reprises disponibles
          // (illimité pour les Premium, cf. isUnlimited côté serveur).
          const peutReprendre = isPremiumUser || (
            (user?.lives ?? 0) >= 1 && continuesUsedRef.current < MAX_CONTINUES
          );
          if (peutReprendre && !isOfflineGame && gameId) setTimeout(() => setOfferContinue(true), 1200);
          else setTimeout(() => finishSurvivalRef.current?.(), 1500);
          return;
        }
        // En mode survie : avance automatiquement après 2s
        setTimeout(() => nextQuestionRef.current?.(), 2000);
      }
      // En mode classique : le joueur doit cliquer CONTINUER (pas d'auto-avance)
    }
  }, [revealed, isSurvivalMode]);

  const { timeLeft, start, pause, reset } = useTimer(
    isSurvivalMode ? TIME_SURVIVAL_START : TIME_CLASSIC,
    handleTimeUp
  );

  useEffect(() => {
    if (timeLeft <= 5 && timeLeft > 0 && !revealed) {
      playTick();
    }
  }, [timeLeft, revealed]);

  // Intercepter le démarrage si hors-ligne
  const handleStartGame = useCallback(() => {
    if (!isOnline) {
      const cached = loadQuestionsFromCache();
      if (!cached || cached.length < 5) {
        toast.error('Hors-ligne : aucune question en cache. Connectez-vous d\'abord.');
        return;
      }
      setShowOfflineModal(true);
    } else {
      setGameStarted(true);
    }
  }, [isOnline]);

  const confirmOfflineGame = useCallback(() => {
    setShowOfflineModal(false);
    setIsOfflineGame(true);
    setGameStarted(true);
  }, []);

  // Chargement des questions (déclenché uniquement après choix de difficulté)
  useEffect(() => {
    if (!gameStarted) return;
    const limit = isSurvivalMode ? SURVIVAL_QUESTIONS : TOTAL_QUESTIONS;
    const diffParam = selectedDifficulty !== 'mixte' ? `&difficulte=${selectedDifficulty}` : '';

    const fetchQuestions = async () => {
      setLoading(true);

      // ── Mode hors-ligne : utiliser le cache ──
      if (isOfflineGame) {
        const cached = loadQuestionsFromCache();
        if (cached && cached.length > 0) {
          const shuffled = [...cached].sort(() => Math.random() - 0.5).slice(0, limit);
          setQuestions(shuffled.map(shuffleOptions));
        } else {
          setQuestions(DEMO_QUESTIONS.map(shuffleOptions));
        }
        setLoading(false);
        return;
      }

      // ── Mode en ligne : utiliser startSolo (inclut la déduplication par historique) ──
      try {
        const body = { nbQuestions: limit };
        if (selectedDifficulty !== 'mixte') body.difficulte = selectedDifficulty;
        const { data } = await api.post('/games/solo/start', body);
        const rawQuestions = data.questions || [];
        // Mettre en cache pour les prochaines sessions hors-ligne
        saveQuestionsToCache(rawQuestions);
        setQuestions(rawQuestions.map(shuffleOptions));
        setGameId(data.gameId || null);
      } catch (err) {
        // 402 : plus de vie pour lancer une survie
        if (err?.response?.status === 402) {
          toast.error(err.response.data?.message || 'Plus de vie disponible');
          navigate('/');
          return;
        }
        console.error('[startSolo] Erreur API :', err?.response?.status, err?.message);
        const cached = loadQuestionsFromCache();
        if (cached && cached.length >= 5) {
          const shuffled = [...cached].sort(() => Math.random() - 0.5).slice(0, limit);
          setQuestions(shuffled.map(shuffleOptions));
        } else {
          setQuestions(DEMO_QUESTIONS.map(shuffleOptions));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [gameStarted, isOfflineGame]);

  // Démarrer/réinitialiser le timer à chaque nouvelle question
  useEffect(() => {
    if (!loading && questions.length > 0 && !gameOver) {
      reset(Math.round(currentTimePerQRef.current));
      start();
    }
  }, [loading, currentIndex, gameOver]);

  // 🎬 Animation victoire (confetti) quand game se termine
  useEffect(() => {
    if (gameOver && !isSurvivalMode) {
      const percentage = results.length > 0 ? Math.round((results.filter((r) => r.correct).length / results.length) * 100) : 0;
      if (percentage >= 70) {
        setTimeout(() => {
          animations.createConfetti();
        }, 500);
      }
    }
  }, [gameOver, isSurvivalMode, results.length]);

  // 🎬 Animation level up — confettis réservés aux victoires (pas sur une défaite)
  useEffect(() => {
    if (levelUpData?.newLevel) {
      const percentage = results.length > 0 ? Math.round((results.filter((r) => r.correct).length / results.length) * 100) : 0;
      const won = !isSurvivalMode && percentage >= 70;
      setTimeout(() => {
        animations.animateLevelUp(levelUpData.newLevel - 1, levelUpData.newLevel, won);
      }, 800);
    }
  }, [levelUpData?.newLevel, results, isSurvivalMode]);

  const handleAnswer = useCallback(async (answerIndex) => {
    if (revealed || selectedAnswer !== null) return;

    pause();
    setSelectedAnswer(answerIndex);
    setRevealed(true);

    const q = questions[currentIndex];
    const correctIdx = q.reponseCorrecte ?? q.correctAnswer ?? 0;
    const isCorrect  = answerIndex === correctIdx;

    // Progression difficulté survival : -0.5s toutes les 5 questions
    if (isSurvivalMode) {
      const newTotal = totalAnsweredRef.current + 1;
      totalAnsweredRef.current = newTotal;
      if (newTotal % 5 === 0) {
        const newTime = Math.max(TIME_SURVIVAL_MIN, currentTimePerQRef.current - 0.5);
        currentTimePerQRef.current = newTime;
        setCurrentTimePerQ(newTime);
      }
    }

    if (isCorrect) {
      const streakBonus  = Math.min(streak, 4);
      const baseTime     = isSurvivalMode ? Math.round(currentTimePerQRef.current) : TIME_CLASSIC;
      const maxTimeBonus = isSurvivalMode ? 50 : 50;
      const timeBonus    = Math.floor((timeLeft / baseTime) * maxTimeBonus);
      // Même base que le serveur : facile=100, moyenne/moyen=200, difficile=300
      const diff = q.difficulte || q.difficulty || 'facile';
      const basePoints = diff === 'difficile' ? 300 : diff === 'moyenne' || diff === 'moyen' ? 200 : 100;
      const streakPct  = Math.min(streak * 0.1, 0.5); // +10% par réponse consécutive, max +50%
      const points     = Math.round((basePoints + timeBonus) * (1 + streakPct));

      setScore((s) => s + points);
      setStreak((s) => s + 1);
      setLastPoints(points);
      setShowScorePopup(true);
      playCorrect();
      setTimeout(() => setShowScorePopup(false), 1200);
      // Auto-avance uniquement en mode survie ; en classique l'user appuie sur CONTINUER
      if (isSurvivalMode) {
        setTimeout(() => nextQuestionRef.current?.(), 1500);
      }
      setResults((r) => [...r, { correct: true, points, timeBonus }]);
    } else {
      setStreak(0);
      playIncorrect();

      if (isSurvivalMode) {
        const newLives = livesRef.current - 1;
        livesRef.current = newLives;
        setLives(newLives);
        if (newLives <= 0) {
          const peutReprendre = isPremiumUser || (
            (user?.lives ?? 0) >= 1 && continuesUsedRef.current < MAX_CONTINUES
          );
          if (peutReprendre && !isOfflineGame && gameId) setTimeout(() => setOfferContinue(true), 1200);
          else setTimeout(() => finishSurvivalRef.current?.(), 1500);
        } else {
          setTimeout(() => nextQuestionRef.current?.(), 1500);
        }
      }

      setResults((r) => [...r, { correct: false, points: 0 }]);
    }

    if (!isOfflineGame) {
      // Les options sont mélangées côté client pour l'affichage (shuffleOptions) :
      // answerIndex est la position dans q.options (affiché), pas dans q.choix
      // (ordre d'origine connu du serveur). bonne_reponse côté serveur est relatif
      // à q.choix, donc on reconvertit l'index avant d'envoyer la réponse — sinon
      // le serveur compare un index mélangé à un index d'origine et invalide la
      // quasi-totalité des bonnes réponses (bug trouvé le 23/10/2026).
      const originalChoix  = Array.isArray(q.choix) ? q.choix : q.options;
      const serverAnswerIdx = originalChoix.indexOf(q.options[answerIndex]);
      try {
        await api.post('/games/solo/answer', {
          gameId,
          questionId: q._id || q.id,
          answer:     serverAnswerIdx,
          timeMs:     (Math.round(currentTimePerQRef.current) - timeLeft) * 1000,
          currentStreak: streak,
        });
      } catch {
        // L'appel n'a pas abouti : on mémorise la réponse pour la faire valider
        // par le serveur à la fin de la partie (aucun point n'est réclamé ici).
        missedAnswersRef.current.push({
          questionId: q._id || q.id,
          answer:     serverAnswerIdx,
          timeMs:     (Math.round(currentTimePerQRef.current) - timeLeft) * 1000,
        });
      }
    }
  }, [revealed, selectedAnswer, questions, currentIndex, streak, timeLeft, pause, isSurvivalMode, gameId]);

  const useHint = useCallback(async () => {
    if (hintUsed || revealed || (user?.hints ?? 0) <= 0) return;
    const q = questions[currentIndex];
    const correctIdx = q.reponseCorrecte ?? q.correctAnswer ?? 0;
    const wrongIndices = [0, 1, 2, 3].filter(i => i !== correctIdx);
    const toEliminate = wrongIndices.sort(() => Math.random() - 0.5).slice(0, 2);
    setEliminatedOptions(toEliminate);
    setHintUsed(true);
    try {
      const { data } = await api.post('/shop/use-hint');
      updateUser({ ...user, hints: data.hintsLeft });
    } catch {
      updateUser({ ...user, hints: Math.max(0, (user?.hints ?? 1) - 1) });
    }
  }, [hintUsed, revealed, user, questions, currentIndex, updateUser]);

  const nextQuestion = useCallback(async () => {
    setEliminatedOptions([]);
    setHintUsed(false);
    const limit = isSurvivalMode ? SURVIVAL_QUESTIONS : TOTAL_QUESTIONS;
    if (currentIndex + 1 >= limit || currentIndex + 1 >= questions.length) {
      pause();
      playVictory();
      setGameOver(true);
      if (user && !isOfflineGame) {
        const correctCount = results.filter((r) => r.correct).length;
        try {
          const { data } = await api.post('/games/solo/end', { gameId, score, correctAnswers: correctCount, missedAnswers: missedAnswersRef.current });
          if (data.user) {
            const merged = { ...user, ...data.user };
            checkLevelUp(user.level || user.niveau || 1, merged);
            updateUser(merged);
          }
          if (data.streakCheckIn) {
            toast.success(`+${data.streakCheckIn.coinsEarned} pièces — Streak jour ${data.streakCheckIn.streakDays} ! 🔥`);
          }
        } catch (err) {
          console.error('❌ Erreur endSolo:', err.message);
          // Score est sauvegardé localement même en cas d'erreur serveur (pas de niveau
          // serveur disponible ici, donc pas de détection de level-up sur ce chemin)
          updateUser({ ...user, coins: (user.coins || 0) + Math.round(score / 5) });
        }
      }
    } else {
      setCurrentIndex((i) => i + 1);
      setSelectedAnswer(null);
      setRevealed(false);
    }
  }, [currentIndex, questions.length, pause, score, user, updateUser, gameId, checkLevelUp, playVictory, isSurvivalMode]);

  // Rachat d'une chance : 1 vie du stock contre 1 vie dans la partie
  const handleContinue = async () => {
    try {
      const { data } = await api.post('/games/solo/continue', { gameId });
      continuesUsedRef.current = data.continuesUsed ?? (continuesUsedRef.current + 1);
      if (data.lives != null) updateUser({ ...user, lives: data.lives });
      livesRef.current = data.livesInGame ?? 1;
      setLives(livesRef.current);
      setOfferContinue(false);
      toast.success(data.message || 'Tu repars avec 1 vie !');
      setTimeout(() => nextQuestionRef.current?.(), 400);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Reprise impossible');
      setOfferContinue(false);
      finishSurvivalRef.current?.();
    }
  };

  nextQuestionRef.current = nextQuestion;

  // Appelé quand le survival se termine par perte de toutes les vies
  // (les deux autres chemins de fin passent par nextQuestion qui appelle déjà endSolo)
  finishSurvivalRef.current = async () => {
    pause();
    playVictory();
    if (user && !isOfflineGame && gameId) {
      const correctCount = results.filter((r) => r.correct).length;
      try {
        const { data } = await api.post('/games/solo/end', { gameId, score, correctAnswers: correctCount, isSurvival: isSurvivalMode, missedAnswers: missedAnswersRef.current });
        if (data.user) {
          const merged = { ...user, ...data.user };
          checkLevelUp(user.level || user.niveau || 1, merged);
          updateUser(merged);
        }
        if (data.streakCheckIn) {
          toast.success(`+${data.streakCheckIn.coinsEarned} pièces — Streak jour ${data.streakCheckIn.streakDays} ! 🔥`);
        }
      } catch (err) {
        console.error('❌ Erreur finishSurvival:', err.message);
        // Score est sauvegardé localement même en cas d'erreur serveur (pas de niveau
        // serveur disponible ici, donc pas de détection de level-up sur ce chemin)
        updateUser({ ...user, coins: (user.coins || 0) + Math.round(score / 5) });
      }
    }
    setGameOver(true);
  };

  const handleReplay = async () => {
    const limit = isSurvivalMode ? SURVIVAL_QUESTIONS : TOTAL_QUESTIONS;
    setScore(0);
    setStreak(0);
    setCurrentIndex(0);
    setGameOver(false);
    setSelectedAnswer(null);
    setRevealed(false);
    setResults([]);
    setGameId(null);
    if (isSurvivalMode) {
      const resetLives = user?.lives ?? SURVIVAL_LIVES;
      setLives(resetLives);
      livesRef.current = resetLives;
      missedAnswersRef.current = [];
      continuesUsedRef.current = 0;
      setOfferContinue(false);
      setCurrentTimePerQ(TIME_SURVIVAL_START);
      currentTimePerQRef.current = TIME_SURVIVAL_START;
      totalAnsweredRef.current   = 0;
    }
    setLoading(true);
    try {
      const body = { nbQuestions: limit };
      if (selectedDifficulty !== 'mixte') body.difficulte = selectedDifficulty;
      const { data } = await api.post('/games/solo/start', body);
      const rawQuestions = data.questions || [];
      saveQuestionsToCache(rawQuestions);
      setQuestions(rawQuestions.map(shuffleOptions));
      setGameId(data.gameId || null);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err.response.data?.message || 'Plus de vie disponible');
        navigate('/');
        return;
      }
      console.error('[handleReplay] Erreur API :', err?.response?.status, err?.message);
      const cached = loadQuestionsFromCache();
      if (cached && cached.length >= 5) {
        // Exclure les questions déjà jouées dans cette session
        const playedIds = new Set(questions.map((q) => q._id || q.id).filter(Boolean));
        const fresh = playedIds.size > 0 ? cached.filter((q) => !playedIds.has(q._id || q.id)) : cached;
        const pool = fresh.length >= 5 ? fresh : cached;
        const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, limit);
        setQuestions(shuffled.map(shuffleOptions));
      } else {
        setQuestions(DEMO_QUESTIONS.map(shuffleOptions));
      }
    } finally {
      setLoading(false);
    }
  };

  // Écran sélection de difficulté (avant le lancement)
  if (!gameStarted) {
    const DIFFS = [
      { key: 'mixte',    label: 'MIXTE',    desc: 'Toutes difficultés mélangées', color: '#f0b429', bg: 'rgba(240,180,41,0.12)', border: 'rgba(240,180,41,0.4)' },
      { key: 'facile',   label: 'FACILE',   desc: 'Questions accessibles',        color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.4)' },
      { key: 'moyen',    label: 'MOYEN',    desc: 'Un peu de challenge',          color: '#f97316', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.4)' },
      { key: 'difficile',label: 'DIFFICILE',desc: 'Réservé aux experts',          color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.4)' },
    ];
    return (
      <div className="app-container flex flex-col min-h-screen">
        <Navbar showBack title={isSurvivalMode ? 'CHRONOMÉTRÉ' : 'JOUER'} />
        <div className="flex flex-col items-center justify-center flex-1 px-6 gap-6">
          {/* Indicateur hors-ligne */}
          {!isOnline && (
            <div className="w-full flex items-center gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
              <FaWifi className="text-yellow-400 flex-shrink-0" size={16} />
              <p className="text-yellow-300 text-xs">
                Vous êtes <strong>hors-ligne</strong>. Seul le mode entraînement est disponible — scores non comptabilisés.
              </p>
            </div>
          )}

          <div className="text-center">
            <p className="text-white/40 text-xs uppercase tracking-widest mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Choisissez la difficulté
            </p>
            <h2 className="text-2xl font-black text-white uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {isSurvivalMode ? 'Mode Survie' : '10 Questions'}
            </h2>
          </div>

          {/* Modal confirmation hors-ligne */}
          <AnimatePresence>
            {showOfflineModal && (
              <motion.div
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              >
                <motion.div
                  className="bg-qfl-card border border-yellow-500/40 rounded-2xl p-6 w-full max-w-sm flex flex-col gap-4"
                  initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                      <FaWifi className="text-yellow-400" size={22} />
                    </div>
                    <div>
                      <p className="text-white font-black text-base uppercase" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        Mode Hors-ligne
                      </p>
                      <p className="text-yellow-400 text-xs font-bold">Entraînement uniquement</p>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Vous n'êtes pas connecté à Internet. Vous pouvez jouer avec les questions sauvegardées,
                    mais <strong className="text-white">cette partie ne sera pas comptabilisée</strong> dans
                    le classement et vos scores ne seront pas enregistrés.
                  </p>
                  <div className="flex flex-col gap-2">
                    <Button variant="gold" size="md" fullWidth onClick={confirmOfflineGame}>
                      Jouer (Entraînement)
                    </Button>
                    <Button variant="outline" size="md" fullWidth onClick={() => setShowOfflineModal(false)}>
                      Annuler
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="w-full flex flex-col gap-3">
            {DIFFS.map(({ key, label, desc, color, bg, border }) => (
              <motion.button
                key={key}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setSelectedDifficulty(key);
                  if (!isOnline) {
                    const cached = loadQuestionsFromCache();
                    if (!cached || cached.length < 5) {
                      toast.error('Hors-ligne : connectez-vous une fois pour activer le mode entraînement.');
                      return;
                    }
                    setShowOfflineModal(true);
                  } else {
                    setGameStarted(true);
                  }
                }}
                className="w-full py-4 px-5 rounded-2xl flex items-center justify-between transition-all"
                style={{ background: bg, border: `1.5px solid ${border}` }}
              >
                <div className="text-left">
                  <p className="font-black text-base uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif", color }}>{label}</p>
                  <p className="text-white/40 text-xs mt-0.5">{desc}</p>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${color}22`, border: `1px solid ${color}66` }}>
                  <span className="font-black text-sm" style={{ color, fontFamily: "'Rajdhani', sans-serif" }}>→</span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Écran de chargement
  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
          <p className="text-qfl-gold font-bold tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            CHARGEMENT...
          </p>
        </div>
      </div>
    );
  }

  // Écran de fin de partie
  if (gameOver) {
    return (
      <GameOverScreen
        score={score}
        results={results}
        isSurvivalMode={isSurvivalMode}
        livesRemaining={lives}
        isOfflineGame={isOfflineGame}
        difficulty={selectedDifficulty}
        onReplay={handleReplay}
        onHome={() => navigate('/')}
      />
    );
  }

  // Niveau de vitesse survie (pour affichage)
  const speedLevel = Math.floor((TIME_SURVIVAL_START - currentTimePerQ) / 0.5) + 1;

  return (
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '20px' }}>
      <Navbar
        showBack
        onBack={() => navigate('/')}
      />

      {/* Rachat d'une chance après élimination en survie */}
      <AnimatePresence>
        {offerContinue && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-5"
            style={{ background: 'rgba(0,0,0,0.85)' }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="w-full max-w-sm rounded-3xl p-6 text-center"
              style={{ background: '#0d2b1a', border: '1px solid rgba(240,180,41,0.3)' }}
            >
              <div className="text-5xl mb-3">💔</div>
              <h3 className="text-xl font-black text-qfl-gold uppercase tracking-wider mb-2"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Éliminé !
              </h3>
              <p className="text-white/70 text-sm mb-1">
                Score actuel : <strong className="text-white">{score} pts</strong>
              </p>
              <p className="text-white/50 text-xs mb-5">
                {isPremiumUser
                  ? <>👑 Reprise Premium gratuite et illimitée.</>
                  : <>Dépense 1 vie pour repartir et garder ton score.
                      {' '}Il te reste {user?.lives ?? 0} vie{(user?.lives ?? 0) > 1 ? 's' : ''}.</>}
              </p>
              <button
                onClick={handleContinue}
                className="w-full py-3.5 rounded-xl font-black text-sm uppercase tracking-widest mb-3"
                style={{ fontFamily: "'Rajdhani', sans-serif",
                         background: 'linear-gradient(135deg, #f0b429, #c97d08)', color: '#0d2b1a' }}
              >
                {isPremiumUser ? '👑 Continuer — gratuit' : '❤️ Continuer — 1 vie'}
              </button>
              <button
                onClick={() => { setOfferContinue(false); finishSurvivalRef.current?.(); }}
                className="w-full py-2.5 text-white/50 text-xs uppercase tracking-widest hover:text-white/80"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Arrêter et encaisser mon score
              </button>
              <p className="text-white/25 text-[11px] mt-3">
                {isPremiumUser ? 'Reprises illimitées (Premium)' : `Reprise ${continuesUsedRef.current + 1} sur ${MAX_CONTINUES}`}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Bannière hors-ligne */}
      {isOfflineGame && (
        <div className="flex items-center gap-2 py-2 mx-4 rounded-xl mb-1 px-4 bg-yellow-500/10 border border-yellow-500/30">
          <FaWifi className="text-yellow-400 flex-shrink-0" size={13} />
          <p className="text-yellow-300 text-xs font-semibold">
            Mode entraînement hors-ligne — score non comptabilisé
          </p>
        </div>
      )}

      {/* Bandeau mode survie */}
      {isSurvivalMode && (
        <motion.div
          className="flex items-center justify-between gap-2 py-2 mx-4 rounded-xl mb-1 px-4"
          style={{
            background: 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(249,115,22,0.2))',
            border: '1px solid rgba(239,68,68,0.4)',
          }}
        >
          {/* Vies — affiche le nombre exact */}
          <div className="flex items-center gap-1">
            <span style={{ fontSize: 18, lineHeight: 1 }}>❤️</span>
            <span
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: '0.8rem',
                fontWeight: 700,
                color: lives <= 1 ? '#ef4444' : '#fbbf24',
                letterSpacing: '0.08em',
              }}
            >
              {lives}/{userLives}
            </span>
          </div>

          {/* Label */}
          <span
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#f97316',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            ⚡ SURVIE — {currentTimePerQ % 1 === 0 ? currentTimePerQ : currentTimePerQ.toFixed(1)}s/Q
          </span>

          {/* Niveau vitesse */}
          <span
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: '0.7rem',
              fontWeight: 800,
              color: speedLevel > 7 ? '#ef4444' : speedLevel > 4 ? '#f97316' : '#fbbf24',
            }}
          >
            Niv.{speedLevel}
          </span>
        </motion.div>
      )}

      {/* Timer circulaire */}
      <div className="flex justify-center py-4 relative">
        <Timer
          timeLeft={timeLeft}
          totalTime={Math.round(currentTimePerQRef.current)}
          size={90}
        />
        {streak > 1 && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute right-6 top-4 flex flex-col items-center"
          >
            <span className="text-orange-400 text-2xl">🔥</span>
            <span className="text-orange-400 font-bold text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              x{streak}
            </span>
          </motion.div>
        )}
      </div>

      {/* Bouton indice 50/50 — mode classique uniquement */}
      {!isSurvivalMode && (
        <div className="px-4 pb-2 flex justify-end">
          <motion.button
            whileTap={!hintUsed && !revealed && (user?.hints ?? 0) > 0 ? { scale: 0.93 } : {}}
            onClick={useHint}
            disabled={hintUsed || revealed || (user?.hints ?? 0) <= 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all"
            style={{
              background: hintUsed || (user?.hints ?? 0) <= 0
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(240,180,41,0.15)',
              border: `1.5px solid ${hintUsed || (user?.hints ?? 0) <= 0 ? 'rgba(255,255,255,0.1)' : 'rgba(240,180,41,0.4)'}`,
              color: hintUsed || (user?.hints ?? 0) <= 0 ? 'rgba(255,255,255,0.25)' : '#f0b429',
              cursor: hintUsed || revealed || (user?.hints ?? 0) <= 0 ? 'default' : 'pointer',
              fontFamily: "'Rajdhani', sans-serif",
            }}
          >
            <FaLightbulb size={11} />
            50/50
            <span className="ml-0.5 opacity-70">({user?.hints ?? 0})</span>
          </motion.button>
        </div>
      )}

      {/* Question et réponses */}
      <div className="flex-1 px-4 overflow-y-auto pb-4 relative">
        <AnimatePresence mode="wait">
          <QuestionCard
            key={currentIndex}
            question={currentQuestion}
            questionIndex={currentIndex}
            totalQuestions={isSurvivalMode ? questions.length : Math.min(TOTAL_QUESTIONS, questions.length)}
            selectedAnswer={selectedAnswer}
            correctAnswer={currentQuestion?.reponseCorrecte ?? currentQuestion?.correctAnswer}
            revealed={revealed}
            onAnswer={handleAnswer}
            disabled={revealed}
            eliminatedOptions={eliminatedOptions}
          />
        </AnimatePresence>

        <ScorePopup
          points={lastPoints}
          visible={showScorePopup}
          streakBonus={streak > 2}
        />
      </div>

      {/* Bouton continuer — uniquement en mode classique */}
      <AnimatePresence>
        {revealed && !isSurvivalMode && (
          <motion.div
            className="px-4 py-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Button variant="gold" size="lg" fullWidth onClick={nextQuestion}>
              {currentIndex + 1 >= Math.min(TOTAL_QUESTIONS, questions.length)
                ? 'VOIR LES RÉSULTATS'
                : 'CONTINUER'}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {levelUpData && (
        <LevelUpModal
          visible={!!levelUpData}
          newLevel={levelUpData.newLevel}
          coinsEarned={levelUpData.coinsEarned}
          unlockedFeature={levelUpData.unlockedFeature}
          onClose={clearLevelUp}
        />
      )}
    </div>
  );
}

// ─── Écran de fin de partie ──────────────────────────────────────────────────
function PushPrompt({ onDone }) {
  const { subscribe, isSupported } = usePushNotification();
  const [loading, setLoading] = useState(false);

  if (!isSupported || Notification.permission !== 'default') return null;
  const alreadyAsked = localStorage.getItem('push_asked');
  if (alreadyAsked) return null;

  const handleAccept = async () => {
    setLoading(true);
    localStorage.setItem('push_asked', '1');
    await subscribe();
    setLoading(false);
    onDone();
  };
  const handleSkip = () => {
    localStorage.setItem('push_asked', '1');
    onDone();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-4 rounded-2xl p-4 border"
      style={{ background: 'rgba(240,180,41,0.08)', borderColor: 'rgba(240,180,41,0.3)' }}
    >
      <div className="flex items-start gap-3">
        <span style={{ fontSize: '1.8rem' }}>🔔</span>
        <div className="flex-1">
          <p className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Reste dans la course !
          </p>
          <p className="text-white/50 text-xs mt-1">
            Reçois une notif quand un tournoi commence ou qu'un adversaire te défie.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleAccept}
              disabled={loading}
              className="flex-1 py-2 rounded-xl text-xs font-black uppercase tracking-wider"
              style={{ background: 'linear-gradient(135deg,#f0b429,#c97d08)', color: '#0d2b1a' }}
            >
              {loading ? '...' : 'Activer'}
            </button>
            <button
              onClick={handleSkip}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white/40 border border-white/10"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function GameOverScreen({ score, results, isSurvivalMode, livesRemaining, isOfflineGame, difficulty, onReplay, onHome }) {
  const [showPushPrompt, setShowPushPrompt] = useState(true);
  const correctCount = results.filter((r) => r.correct).length;
  const total        = results.length;
  const percentage   = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const livesLost    = SURVIVAL_LIVES - livesRemaining;
  const difficultyLabel = {
    mixte: 'Mixte', facile: 'Facile', moyen: 'Moyen', difficile: 'Difficile',
  }[difficulty] || 'Mixte';

  const getRating = () => {
    if (isSurvivalMode) {
      if (total >= 40) return { label: 'Légende',    color: '#f0b429', icon: '👑' };
      if (total >= 25) return { label: 'Expert',     color: '#22c55e', icon: '⭐' };
      if (total >= 15) return { label: 'Bien joué',  color: '#3b82f6', icon: '👍' };
      return               { label: 'Continue !',  color: '#94a3b8', icon: '📚' };
    }
    // Seuils alignés sur isWin (>=70%) pour ne jamais afficher un label positif
    // ("Bien joué") en même temps que la bannière DÉFAITE.
    if (percentage >= 90) return { label: 'Légende',        color: '#f0b429', icon: '👑' };
    if (percentage >= 70) return { label: 'Bien joué',       color: '#22c55e', icon: '⭐' };
    if (percentage >= 50) return { label: 'Presque !',       color: '#f97316', icon: '💪' };
    return                       { label: 'Peut mieux faire', color: '#94a3b8', icon: '📚' };
  };

  const rating  = getRating();
  const isWin   = !isSurvivalMode && percentage >= 70;

  const generateScoreImage = async () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 800;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No 2D context');

      // Fond avec gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, 800);
      gradient.addColorStop(0, '#003a70');
      gradient.addColorStop(1, '#f0b429');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 600, 800);

      // Texte blanc
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';

      // Titre
      ctx.font = 'bold 32px Arial';
      ctx.fillText('QUIZFOOT', 300, 60);

      // Score
      ctx.font = 'bold 80px Arial';
      ctx.fillText(String(score), 300, 220);

      // Points
      ctx.font = 'bold 24px Arial';
      ctx.fillText('POINTS', 300, 280);

      // Résultat
      ctx.font = '18px Arial';
      ctx.fillText(`${correctCount}/${total} correctes`, 300, 380);

      // Pseudo
      ctx.font = '16px Arial';
      ctx.fillText(user?.pseudo || 'Joueur', 300, 420);

      // Convertir en blob
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('toBlob failed'));
        }, 'image/png', 0.8);
      });

      return blob;
    } catch (error) {
      console.error('Image error:', error);
      throw error;
    }
  };

  const handleShare = async () => {
    try {
      animations.showNotification('Partage en cours...', 'info', 1000);

      // Partager juste du TEXTE (pas d'image pour éviter les lags)
      const difficultyLabel = {
        mixte: 'Mixte', facile: 'Facile', moyen: 'Moyen', difficile: 'Difficile',
      }[selectedDifficulty] || 'Mixte';

      if (navigator.share) {
        await navigator.share({
          title: 'QuizFoot League',
          text: `👑 J'ai obtenu ${score} pts au QuizFoot League !\n✅ ${correctCount}/${total} bonnes réponses (${percentage}%)\n🎯 Difficulté : ${difficultyLabel}\n⚽ Relève le défi !\n\n➡️ Joue maintenant: https://quizfootleague.dekanisoftware.com/`,
        });
        setTimeout(() => {
          if (isWin) animations.createConfetti();
          animations.showNotification('Score partagé ! 🎉', 'success', 2000);
        }, 200);
      } else {
        animations.showNotification('Partage non supporté', 'error', 2000);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Partage échoué :', err);
        animations.showNotification('Erreur lors du partage', 'error', 2000);
      }
    }
  };

  return (
    <motion.div
      className="app-container flex flex-col min-h-screen px-5 pt-6 pb-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Bannière VICTOIRE / DÉFAITE (mode classique uniquement) */}
      {!isSurvivalMode && !isOfflineGame && (
        <motion.div
          className="text-center mb-1"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, delay: 0.1 }}
        >
          <span
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: '1.5rem',
              fontWeight: 900,
              letterSpacing: '0.22em',
              color: isWin ? '#22c55e' : '#ef4444',
              textShadow: isWin
                ? '0 0 24px rgba(34,197,94,0.6)'
                : '0 0 24px rgba(239,68,68,0.6)',
            }}
          >
            {isWin ? '✅ VICTOIRE' : '❌ DÉFAITE'}
          </span>
        </motion.div>
      )}

      {/* Icône résultat */}
      <div className="flex flex-col items-center pt-2 pb-2">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
          className="text-5xl mb-2"
        >
          {rating.icon}
        </motion.div>
        <h2
          className="text-3xl font-black uppercase tracking-widest"
          style={{ fontFamily: "'Rajdhani', sans-serif", color: rating.color }}
        >
          {rating.label}
        </h2>
        {isSurvivalMode && (
          <p className="text-white/40 text-xs mt-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            MODE SURVIE
          </p>
        )}
      </div>

      {/* Score principal */}
      <motion.div
        className="bg-black/40 rounded-3xl p-4 border border-white/10 mb-3 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="text-white/60 text-sm mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          SCORE FINAL
        </div>
        <motion.div
          className="text-5xl font-black text-qfl-gold"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.4, type: 'spring' }}
        >
          {score}
        </motion.div>
        <div className="text-white/50 text-sm mt-1">points</div>

        <div
          className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            background: 'rgba(240,180,41,0.12)',
            border: '1px solid rgba(240,180,41,0.35)',
            color: '#f0b429',
          }}
        >
          🎯 {difficultyLabel}
        </div>

        <div className="flex justify-around mt-5">
          {isSurvivalMode ? (
            <>
              <div className="flex flex-col items-center">
                <span className="text-2xl font-black text-qfl-green" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {total}
                </span>
                <span className="text-white/50 text-xs">Questions</span>
              </div>
              <div className="w-px bg-white/10" />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {correctCount}
                </span>
                <span className="text-white/50 text-xs">Correctes</span>
              </div>
              <div className="w-px bg-white/10" />
              <div className="flex flex-col items-center">
                <div className="flex gap-0.5 justify-center">
                  {Array.from({ length: SURVIVAL_LIVES }).map((_, i) => (
                    <span key={i} style={{ fontSize: 18, opacity: i < livesRemaining ? 1 : 0.2 }}>❤️</span>
                  ))}
                </div>
                <span className="text-white/50 text-xs">Vies restantes</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center">
                <span className="text-2xl font-black text-qfl-green" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {correctCount}/{total}
                </span>
                <span className="text-white/50 text-xs">Correctes</span>
              </div>
              <div className="w-px bg-white/10" />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {percentage}%
                </span>
                <span className="text-white/50 text-xs">Précision</span>
              </div>
              <div className="w-px bg-white/10" />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-black text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {results.reduce((s, r) => s + (r.timeBonus || 0), 0)}
                </span>
                <span className="text-white/50 text-xs">Bonus temps</span>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Récapitulatif rapide */}
      <div className="flex gap-2 mb-4 flex-wrap justify-center">
        {results.map((r, i) => (
          <motion.div
            key={i}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.05 * Math.min(i, 20), type: 'spring', stiffness: 300 }}
            style={{
              width: 32, height: 32,
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 900,
              fontSize: '0.75rem',
              background: r.correct ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)',
              border: `2px solid ${r.correct ? '#22c55e' : '#ef4444'}`,
              color: r.correct ? '#22c55e' : '#ef4444',
              boxShadow: r.correct ? '0 0 8px rgba(34,197,94,0.3)' : '0 0 8px rgba(239,68,68,0.3)',
            }}
          >
            {i + 1}
          </motion.div>
        ))}
      </div>

      {/* Boutons */}
      <div className="flex flex-col gap-3 mt-auto">
        <motion.button
          onClick={handleShare}
          whileTap={{ scale: 0.97 }}
          className="w-full py-3.5 rounded-2xl font-bold text-base uppercase tracking-wider flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(135deg, #1d9bf0, #1a8cd8)',
            boxShadow: '0 4px 15px rgba(29,155,240,0.4)',
            fontFamily: "'Rajdhani', sans-serif",
            color: '#fff',
          }}
        >
          <FaShareAlt size={16} />
          PARTAGER MON SCORE
        </motion.button>

        {/* Avertissement hors-ligne */}
        {isOfflineGame && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
            <FaWifi className="text-yellow-400 flex-shrink-0" size={16} />
            <p className="text-yellow-300 text-xs leading-relaxed">
              Partie jouée <strong>hors-ligne</strong> — score et progression non enregistrés.
              Reconnectez-vous pour que vos parties comptent dans le classement.
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="gold" size="lg" fullWidth onClick={onReplay}>
            <FaRedo size={16} />
            REJOUER
          </Button>
          <Button variant="outline" size="md" fullWidth onClick={onHome}>
            <FaHome size={16} />
            ACCUEIL
          </Button>
        </div>

        {/* Prompt push — affiché une seule fois après la 1ère partie */}
        {showPushPrompt && !isOfflineGame && (
          <PushPrompt onDone={() => setShowPushPrompt(false)} />
        )}
      </div>
    </motion.div>
  );
}

export default Quiz;
